import { useMemo, useState } from "react";

import { useNavigate } from "@tanstack/react-router";
import { FilePlus2, Loader2 } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCan } from "@/lib/auth/useCan";

import type { ContractDetail } from "@/features/contracts/api";
import { useCatalog } from "../api";
import { buildIndex, type LineRow } from "../calc";
import { formatPrice } from "../format";
import { writePrefill, type Header } from "../handoff";

const CATEGORY_LABELS: Record<string, string> = {
  subscription: "Subscription",
  usage: "Usage",
  pass_thru: "Pass-thru",
  services: "Services",
  other: "Other",
};

const newLocalId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

/** A contract line whose item is no longer available and needs the user's input. */
interface Unresolved {
  key: string;
  name: string;
  qty: number;
  unit_price: number;
  reason: "removed" | "archived" | "base_fee";
}

type Resolution = { optionId: string | null; drop: boolean };

/**
 * Button (contract detail view) that seeds the Proposals calculator from the
 * contract. Contract lines are matched to current catalog items by Cinch item
 * UUID; any line whose item was removed/archived (and the base fee if its
 * package isn't found) is surfaced in a modal to substitute or drop before we
 * hand off. Only rendered for users who can both view and create proposals.
 */
export function CreateProposalFromContractButton({ contract }: { contract: ContractDetail }) {
  const canRead = useCan("proposals.read");
  const canWrite = useCan("proposals.write");
  const navigate = useNavigate();
  const catalogQ = useCatalog();
  const catalog = catalogQ.data;

  const [open, setOpen] = useState(false);
  const [unresolved, setUnresolved] = useState<Unresolved[]>([]);
  const [carried, setCarried] = useState<LineRow[]>([]);
  const [header, setHeader] = useState<Header | null>(null);
  const [overageNames, setOverageNames] = useState<string[]>([]);
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({});

  const idx = useMemo(() => (catalog ? buildIndex(catalog) : null), [catalog]);

  // Current products for the substitution picker — same rules as the calculator.
  const productOptions: ComboOption[] = useMemo(() => {
    if (!catalog) return [];
    const items = catalog.items
      .filter((i) => !i.is_archived)
      .map((i) => ({
        id: `item:${i.id}`,
        label: i.name,
        subtitle: CATEGORY_LABELS[i.category ?? ""] ?? "Item",
        group: "Items",
        keywords: i.sku_cinch ?? "",
      }));
    const pkgs = catalog.packages
      .filter((p) => p.proposal_eligible && !p.is_archived)
      .map((p) => ({
        id: `pkg:${p.id}`,
        label: p.name,
        subtitle: "Package",
        group: "Packages",
        badge: p.pricing_type === "flat_fee" ? "Flat" : "Per Unit",
        keywords: p.sku ?? "",
      }));
    return [...items, ...pkgs];
  }, [catalog]);

  function buildNotes(subLines: string[], overages: string[]): string {
    const today = new Date().toISOString().slice(0, 10);
    const src = contract.company_name ? ` (${contract.company_name})` : "";
    const lines = [`Created from contract \`${contract.id}\`${src} on ${today}.`];
    if (subLines.length) lines.push("", "**Line item changes:**", ...subLines);
    if (overages.length) {
      lines.push(
        "",
        `_Overage rates not carried over (proposals don't model overage): ${overages.join(", ")}._`,
      );
    }
    return lines.join("\n");
  }

  /** Map the contract → carried lines + unresolved rows + header. */
  function analyze() {
    if (!catalog || !idx) return null;
    const carriedLines: LineRow[] = [];
    const missing: Unresolved[] = [];
    const overages: string[] = [];

    for (const li of contract.contract_line_items ?? []) {
      const name = li.item_detail?.name ?? "(unnamed item)";
      const qty = li.qty ?? 1;
      const price = li.price_per_unit ?? 0;
      if ((li.overage_price_per_unit ?? 0) > 0) overages.push(name);

      const catItem = li.item ? idx.itemById.get(li.item) : undefined;
      if (catItem && !catItem.is_archived) {
        carriedLines.push({ _id: newLocalId(), item_id: li.item ?? undefined, quantity: qty, unit_price: price });
      } else {
        missing.push({
          key: li.uuid ?? `${name}-${missing.length}`,
          name,
          qty,
          unit_price: price,
          reason: catItem ? "archived" : "removed",
        });
      }
    }

    // Base fee → the "Base Fee" package (matched by name); else an unresolved row.
    const baseFee = contract.base_fee ?? 0;
    if (baseFee > 0) {
      const eligible = catalog.packages.filter((p) => p.proposal_eligible && !p.is_archived);
      const basePkg =
        eligible.find((p) => (p.name ?? "").trim().toLowerCase() === "base fee") ??
        eligible.find((p) => (p.name ?? "").toLowerCase().includes("base fee"));
      if (basePkg) {
        carriedLines.push({ _id: newLocalId(), package_id: basePkg.id, quantity: 1, unit_price: baseFee });
      } else {
        missing.push({ key: "__base_fee__", name: "Base fee", qty: 1, unit_price: baseFee, reason: "base_fee" });
      }
    }

    const closer = contract.closer_hubspot_owner_id
      ? catalog.closers.find(
          (c) => !!c.hubspot_owner_id && c.hubspot_owner_id === contract.closer_hubspot_owner_id,
        )
      : undefined;

    const hdr: Header = {
      customer_name: contract.company_name ?? contract.company_dba ?? "",
      proposal_name: contract.company_name ?? "",
      contract_months: contract.term_months ?? 12,
      closer_id: closer?.id ?? "",
      locations: null,
      is_auto_renew: true,
      renewal_term: "SAME_TERM",
      renewal_term_months: null,
      mtm_uplift_pct: null,
      billing_effective_from: contract.billing_effective_from ?? null,
      notes: "",
    };

    return { carriedLines, missing, overages, hdr };
  }

  function handoff(lineItems: LineRow[], hdr: Header) {
    writePrefill({ header: hdr, lineItems, discountRows: [] });
    navigate({ to: "/proposals" });
  }

  function handleClick() {
    const result = analyze();
    if (!result) return;
    const { carriedLines, missing, overages, hdr } = result;
    if (missing.length === 0) {
      handoff(carriedLines, { ...hdr, notes: buildNotes([], overages) });
      return;
    }
    setCarried(carriedLines);
    setUnresolved(missing);
    setOverageNames(overages);
    setHeader(hdr);
    setResolutions(Object.fromEntries(missing.map((m) => [m.key, { optionId: null, drop: false }])));
    setOpen(true);
  }

  const allResolved = unresolved.every((m) => {
    const r = resolutions[m.key];
    return r && (r.drop || r.optionId);
  });

  function handleConfirm() {
    if (!idx || !header) return;
    const finalLines = [...carried];
    const subLines: string[] = [];
    for (const m of unresolved) {
      const r = resolutions[m.key];
      if (!r || r.drop) {
        subLines.push(`- Dropped "${m.name}"`);
        continue;
      }
      const optionId = r.optionId;
      if (!optionId) continue; // guarded by allResolved
      const sep = optionId.indexOf(":");
      const kind = optionId.slice(0, sep);
      const refId = optionId.slice(sep + 1);
      if (kind === "item") {
        finalLines.push({ _id: newLocalId(), item_id: refId, quantity: m.qty, unit_price: m.unit_price });
        subLines.push(`- Substituted "${m.name}" → "${idx.itemById.get(refId)?.name ?? refId}"`);
      } else {
        finalLines.push({ _id: newLocalId(), package_id: refId, quantity: m.qty, unit_price: m.unit_price });
        subLines.push(`- Substituted "${m.name}" → "${idx.pkgById.get(refId)?.name ?? refId}"`);
      }
    }
    setOpen(false);
    handoff(finalLines, { ...header, notes: buildNotes(subLines, overageNames) });
  }

  if (!canRead || !canWrite) return null;

  const carriedCount = (contract.contract_line_items?.length ?? 0) - unresolved.filter((m) => m.reason !== "base_fee").length;
  const totalLines = contract.contract_line_items?.length ?? 0;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={catalogQ.isLoading || catalogQ.isError || !catalog}
        title={
          catalogQ.isError
            ? "Can't load the proposals catalog"
            : "Build a proposal from this contract"
        }
      >
        {catalogQ.isLoading ? (
          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
        ) : (
          <FilePlus2 className="mr-1 h-4 w-4" />
        )}
        Create Proposal
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Some items need a substitute</DialogTitle>
            <DialogDescription>
              {carriedCount} of {totalLines} contract line{totalLines === 1 ? "" : "s"} carried over directly.
              The items below are no longer in the catalog — pick a current item/package or drop each one.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
            {unresolved.map((m) => {
              const r = resolutions[m.key] ?? { optionId: null, drop: false };
              return (
                <div key={m.key} className="rounded-lg border p-3">
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{m.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {m.qty.toLocaleString()} × {formatPrice(m.unit_price)}
                        {" · "}
                        {m.reason === "archived"
                          ? "archived in catalog"
                          : m.reason === "base_fee"
                            ? "no “Base Fee” package found"
                            : "no longer in catalog"}
                      </p>
                    </div>
                    <Button
                      variant={r.drop ? "default" : "outline"}
                      size="sm"
                      onClick={() =>
                        setResolutions((prev) => ({
                          ...prev,
                          [m.key]: { optionId: null, drop: !r.drop },
                        }))
                      }
                    >
                      {r.drop ? "Dropped" : "Drop line"}
                    </Button>
                  </div>
                  {!r.drop && (
                    <Combobox
                      options={productOptions}
                      value={r.optionId}
                      onChange={(id) =>
                        setResolutions((prev) => ({ ...prev, [m.key]: { optionId: id, drop: false } }))
                      }
                      placeholder="Substitute with…"
                      searchPlaceholder="Search items & packages…"
                    />
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!allResolved}>
              Create proposal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
